import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Building2, Users, TrendingUp, Award } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CorporateSignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    company_name: '',
    company_email: '',
    contact_person: '',
    contact_phone: '',
    employee_count: '',
    industry: 'technology',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/corporate/signup`, {
        ...formData,
        employee_count: parseInt(formData.employee_count)
      });
      
      localStorage.setItem('token', response.data.token);
      toast.success('Corporate account created!');
      navigate('/corporate/dashboard');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Corporate Wellness Program
          </h1>
          <p className="text-lg text-gray-600">Transform your workplace with Ayurvedic wellness</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Benefits */}
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold mb-6">Why Choose Our Corporate Program?</h2>
            
            {[
              {
                icon: <Users className="h-8 w-8 text-primary" />,
                title: 'Employee Wellness',
                description: 'Personalized Dosha assessments for every employee'
              },
              {
                icon: <TrendingUp className="h-8 w-8 text-primary" />,
                title: 'Boost Productivity',
                description: 'Healthier employees perform better and take fewer sick days'
              },
              {
                icon: <Award className="h-8 w-8 text-primary" />,
                title: 'Comprehensive Analytics',
                description: 'Track wellness metrics and ROI with detailed reports'
              },
              {
                icon: <Building2 className="h-8 w-8 text-primary" />,
                title: 'Flexible Plans',
                description: 'Scalable solutions for companies of all sizes'
              }
            ].map((benefit, i) => (
              <Card key={i} className="card-lift">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div>{benefit.icon}</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Signup Form */}
          <Card>
            <CardHeader>
              <CardTitle>Get Started</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="company_email">Company Email</Label>
                  <Input
                    id="company_email"
                    type="email"
                    value={formData.company_email}
                    onChange={(e) => setFormData({ ...formData, company_email: e.target.value })}
                    required
                    data-testid="company-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="employee_count">Number of Employees</Label>
                  <Input
                    id="employee_count"
                    type="number"
                    value={formData.employee_count}
                    onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="industry">Industry</Label>
                  <select
                    id="industry"
                    className="w-full border border-gray-200 rounded-lg h-10 px-3"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  >
                    <option value="technology">Technology</option>
                    <option value="finance">Finance</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading} data-testid="corporate-signup-btn">
                  {loading ? 'Creating Account...' : 'Create Corporate Account'}
                </Button>

                <p className="text-sm text-gray-600 text-center">
                  Already have an account?{' '}
                  <a href="/" className="text-primary hover:underline">Login here</a>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CorporateSignupPage;